import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { Button } from '@/components/ui/Button';
import { api } from '@/lib/api';
import {
  RecordingConsentTable,
  RecordingConsentSummary,
} from '@/components/admin/RecordingConsentTable';
import { RecordingConsentDetailModal } from '@/components/admin/RecordingConsentDetailModal';

interface PaginatedRecordingConsents {
  items: RecordingConsentSummary[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

const PAGE_SIZE = 20;

export function RecordingConsentViewer() {
  const [searchParams, setSearchParams] = useSearchParams();
  const userIdFilter = searchParams.get('user_id');

  const [consents, setConsents] = useState<RecordingConsentSummary[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentOnly, setCurrentOnly] = useState(true);
  const [selectedConsentId, setSelectedConsentId] = useState<string | null>(null);

  const fetchConsents = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('per_page', String(PAGE_SIZE));
    params.set('current_only', currentOnly ? 'true' : 'false');
    if (userIdFilter) params.set('user_id', userIdFilter);
    const response = await api.get<PaginatedRecordingConsents>(
      `/admin/recording-consents?${params.toString()}`
    );
    if (response.error) {
      setError(response.error);
      setConsents([]);
    } else if (response.data) {
      setConsents(response.data.items);
      setTotal(response.data.total);
      setTotalPages(response.data.totalPages);
    }
    setIsLoading(false);
  }, [page, currentOnly, userIdFilter]);

  useEffect(() => {
    fetchConsents();
  }, [fetchConsents]);

  useEffect(() => {
    setPage(1);
  }, [currentOnly, userIdFilter]);

  const clearUserFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('user_id');
    setSearchParams(next);
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-600 dark:text-slate-400">
        View signed prayer recording consent forms and signatures for record-keeping.
      </p>

      {userIdFilter && (
        <div className="flex items-center gap-2 text-sm">
          <span className="text-slate-600 dark:text-slate-400">Filtered to one user</span>
          <Button variant="ghost" size="sm" onClick={clearUserFilter}>
            Clear filter
          </Button>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <label className="inline-flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
          <input
            type="checkbox"
            checked={currentOnly}
            onChange={(e) => setCurrentOnly(e.target.checked)}
            className="rounded"
          />
          Show only current consents (one per user)
        </label>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          {total} consent record{total === 1 ? '' : 's'}
        </p>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-800 dark:text-red-400">{error}</p>
          <Button variant="ghost" size="sm" onClick={fetchConsents} className="mt-2">
            Try Again
          </Button>
        </div>
      )}

      <RecordingConsentTable
        consents={consents}
        isLoading={isLoading}
        onView={(consent) => setSelectedConsentId(consent.id)}
      />

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1 || isLoading}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Previous
          </Button>
          <span className="text-sm text-slate-600 dark:text-slate-400">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages || isLoading}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </Button>
        </div>
      )}

      <RecordingConsentDetailModal
        consentId={selectedConsentId}
        onClose={() => setSelectedConsentId(null)}
      />
    </div>
  );
}
